/**
 * client/src/lib/pending-invitations.ts
 *
 * One source of truth for "what is waiting on this person's decision" (#2171).
 *
 * Four independent rails feed the Invitations page, and ALL of them need to be
 * counted in two other places — the nav badge that makes an invitation discoverable
 * without the person happening to visit a page, and the pointer left behind on
 * Connections. Centralising the query keys here means every reader shares
 * react-query's cache: accepting an invitation invalidates one key and the
 * badge drops on its own, with no second fetch and no chance of the readers
 * drifting out of step.
 *
 * Shape is deliberately CONTEXT-over-hook. The counts are fetched exactly once,
 * by `PendingInvitationsProvider` inside the authenticated shell; everything
 * else — including `useVisibleNavItems`, which both navs call — only reads.
 * That keeps the nav builders renderable with no QueryClient in scope (they
 * are pure display logic and their tests treat them that way), and it means a
 * count can never differ between the desktop sidebar and the mobile tabs
 * because they are literally reading the same value.
 *
 * The counts are a plain authenticated read, so they survive a reload and need
 * no live socket — deliberately the load-bearing half of the discovery fix.
 * The WebSocket notice (`use-invitation-push.ts`) is the other half and reaches
 * a LIVE session only; neither this module nor any surface built on it may
 * present that notice as guaranteed reach.
 *
 * HONEST SCOPE of where the count is SHOWN, so nothing here overclaims: the
 * Invitations nav entry is PERSONAL context only (the org list is already at
 * the mobile touch-target ceiling — see nav-items.ts). In org context the
 * standing signal is the badge on the org switcher's "Personal" row, plus the
 * pointer on the Connections page; there is no org-context nav badge.
 */

import { createContext, useContext } from "react";

/**
 * Imported, never retyped, by every reader (the two sections, the provider,
 * the push listener) so a rename breaks the build instead of silently
 * un-invalidating one of them.
 */
export const MEMBERSHIP_REQUESTS_QUERY_KEY = ["membership-requests-incoming"];
export const CONNECTION_REQUESTS_QUERY_KEY = ["connections-incoming-requests"];
export const ORG_CONNECTION_REQUESTS_QUERY_KEY = ["org-connections-incoming-pending"];
/**
 * Intentionally the key the Guardian Setup page has always used, NOT a new one:
 * both surfaces answer the same requests, so they must share one cache entry or
 * answering on one would leave the other — and the badge — stale.
 */
export const GUARDIANSHIPS_QUERY_KEY = ["/api/circle/guardianships"];

export interface PendingInvitationCounts {
  /** An organisation asking this person to hold a role inside it. */
  organisation: number;
  /** Both personal and organisation-targeted connection requests. */
  connection: number;
  /**
   * A person asking this person to be their guardian. Pending guardianships
   * only — an active or former one is a relationship, not a question.
   */
  guardian: number;
  total: number;
  /**
   * True until EVERY read has resolved. Callers that render a count-driven
   * affordance use this to stay silent rather than flash a "0" that could be
   * wrong — an invitation briefly rendered as absent is worse than one
   * rendered a moment late.
   */
  isLoading: boolean;
  /**
   * True when ANY read failed. Load-bearing, not cosmetic: a failed query
   * resolves to `data: undefined`, which counts as zero — so without this a
   * surface would state "no invitations right now" on the strength of a
   * request that never came back. Any surface that would otherwise assert
   * absence must check this first and say it could not load instead.
   */
  isError: boolean;
}

/**
 * Default is zero counts and `isLoading: true` — the honest reading for a tree
 * with no provider above it: nothing is known yet, so nothing is claimed. It
 * is deliberately NOT "0, loaded", which would let a surface state that a
 * person has no invitations when in fact nobody asked.
 */
export const EMPTY_PENDING_INVITATION_COUNTS: PendingInvitationCounts = {
  organisation: 0,
  connection: 0,
  guardian: 0,
  total: 0,
  isLoading: true,
  isError: false,
};

export const PendingInvitationsContext = createContext<PendingInvitationCounts>(
  EMPTY_PENDING_INVITATION_COUNTS,
);

/**
 * Read the counts. Safe to call anywhere; outside the provider it reports the
 * unknown-yet default above rather than throwing, so a nav rendered in
 * isolation degrades to "no badge" instead of to a crash.
 */
export function usePendingInvitationCounts(): PendingInvitationCounts {
  return useContext(PendingInvitationsContext);
}
